import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { reportCrmFailure } from './requestDiagnostics';
import { functions } from '../../firebase';
import { stripUndefinedDeep } from '../../general/helpers';

export const kindSchema = z.enum(['company', 'site', 'contact', 'deal', 'activity']);
export type Kind = z.infer<typeof kindSchema>;
export const activityTypes: Record<string, string> = {
  call: 'Opkald',
  meeting: 'Møde',
  task: 'Opgave',
  'follow-up': 'Opfølgning',
  replenishment: 'Genbestilling',
};
export const labels: Record<Kind, string> = {
  company: 'Virksomheder',
  site: 'Lokationer',
  contact: 'Kontakter',
  deal: 'Salgsmuligheder',
  activity: 'Aktiviteter',
};
const stageSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number(),
  retired: z.boolean(),
});
export const sessionSchema = z.object({
  member: z.object({
    id: z.string(),
    name: z.string(),
    administrator: z.boolean(),
    active: z.boolean(),
    ownActions: z.array(z.string()),
    allActions: z.array(z.string()),
  }),
  pipelines: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      retired: z.boolean(),
      stages: z.array(stageSchema),
    })
  ),
  members: z.array(z.object({ id: z.string(), name: z.string() })),
  version: z.number(),
});
export type Session = z.infer<typeof sessionSchema>;
export const pageCursorSchema = z.object({ offset: z.number(), version: z.number() });
export type PageCursor = z.infer<typeof pageCursorSchema>;
export const listSchema = z.object({
  records: z.array(
    z.object({
      id: z.string(),
      kind: kindSchema,
      title: z.string(),
      ownerId: z.string(),
      version: z.number(),
      updatedAt: z.string(),
      canEdit: z.boolean(),
      summary: z
        .object({
          email: z.string().nullable().optional(),
          phone: z.string().nullable().optional(),
          jobTitle: z.string().nullable().optional(),
          registrationNumber: z.string().nullable().optional(),
          address: z.string().nullable().optional(),
        })
        .optional(),
      companies: z
        .array(
          z.union([
            z.object({ restricted: z.literal(true) }),
            z.object({ id: z.string(), title: z.string() }),
          ])
        )
        .optional(),
      moreCompanies: z.boolean().optional(),
    })
  ),
  next: pageCursorSchema.nullable(),
});
export const detailsSchema = z.object({
  id: z.string(),
  kind: kindSchema,
  title: z.string(),
  ownerId: z.string(),
  version: z.number(),
  everyoneCanRead: z.boolean(),
  details: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])),
  links: z.record(
    z.object({
      id: z.string().optional(),
      title: z.string().optional(),
      restricted: z.literal(true).optional(),
    })
  ),
  permissions: z.record(z.boolean()),
});
export type RecordDetails = z.infer<typeof detailsSchema>;
export const relationshipsSchema = z.object({
  id: z.string(),
  kind: z.enum(['contact', 'company', 'site']),
  version: z.number(),
  canEdit: z.boolean(),
  relationships: z.array(
    z.union([
      z.object({ restricted: z.literal(true), kind: z.enum(['contact', 'company', 'site']) }),
      z.object({ id: z.string(), title: z.string(), kind: z.enum(['contact', 'company', 'site']) }),
    ])
  ),
});
export async function crmRequest<T>(
  command: object,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
): Promise<T> {
  try {
    const response = await functions.httpsCallable('internalCrm', { timeout: 120000 })(
      stripUndefinedDeep(command)
    );
    return schema.parse(response.data);
  } catch (cause) {
    reportCrmFailure('internalCrm', cause);
    throw cause;
  }
}
export async function googleRequest<T>(
  command: object,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
): Promise<T> {
  const action = (command as { action?: string }).action;
  try {
    const response = await functions.httpsCallable('internalCrmGoogle', { timeout: 120000 })(
      stripUndefinedDeep(command)
    );
    const result = schema.parse(response.data);
    if (['complete', 'disconnect', 'calendar', 'syncGmail', 'syncCalendar'].includes(action || ''))
      window.dispatchEvent(new Event('crm-google-status-changed'));
    return result;
  } catch (cause) {
    reportCrmFailure('internalCrmGoogle', cause);
    if (
      action !== 'status' &&
      ['functions/unauthenticated', 'functions/unavailable', 'functions/internal'].includes(
        (cause as { code?: string })?.code || ''
      )
    )
      window.dispatchEvent(
        new CustomEvent('crm-google-status-changed', {
          detail: {
            reconnectRequired:
              (cause as { details?: { googleReconnect?: boolean } })?.details?.googleReconnect ===
              true,
          },
        })
      );
    throw cause;
  }
}
export const savedSchema = z.object({ id: z.string(), version: z.number() });
export const errorMessage = (error: unknown) =>
  error instanceof z.ZodError
    ? 'Svaret kunne ikke læses. Genindlæs visningen og prøv igen.'
    : error instanceof Error
    ? error.message
    : 'Handlingen mislykkedes. Prøv igen.';

/** Keep shared caches fresh without reloading permissions or unrelated mounted panels. */
export async function invalidateCrmData(
  client: QueryClient,
  uid: string,
  families = [
    'detail',
    'list',
    'pipeline',
    'activities',
    'reports',
    'picker',
    'globalSearch',
    'timeline',
    'journal',
    'relationships',
    'duplicates',
    'archive',
    'permissions',
    'offers',
    'operational',
  ]
) {
  await client.invalidateQueries({
    queryKey: ['crm', uid],
    refetchType: 'none',
    predicate: ({ queryKey }) => !['session', 'google', 'savedViews'].includes(String(queryKey[2])),
  });
  if (!families.length) return;
  await client.refetchQueries({
    queryKey: ['crm', uid],
    type: 'active',
    predicate: ({ queryKey }) => families.includes(String(queryKey[2])),
  });
}

export function formatMinor(minor: string, currency: string, locale: string) {
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
  const decimals = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  const padded = minor.padStart(decimals + 1, '0');
  const integer = decimals ? padded.slice(0, -decimals) : padded;
  const fraction = decimals ? padded.slice(-decimals) : '';
  return formatter
    .formatToParts(BigInt(integer))
    .map((part) => (part.type === 'fraction' ? fraction : part.value))
    .join('');
}
